// repo/primary-care-connect/frontend/src/pages/Portal/ClinicAdmin/ClinicProfile/sections/ClinicInformationSection.tsx

import React from "react";

type ClinicInfo = {
  name?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  cityName?: string | null;
  postalCode?: string | null;
  phone?: string | null;
  fax?: string | null;
  website?: string | null;
  administrationEmail?: string | null;
  latitude?: number | null;
  longitude?: number | null;

  // Clinic model/type (dbo.ClinicSetting)
  clinicSettingId?: number | null;
  clinicSettingName?: string | null;
};

type IdName = { id: number; name: string };

function formatSavedAt(val: string | null | undefined): string {
  if (!val) return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

export default function ClinicInformationSection(props: {
  clinic: ClinicInfo;
  editMode: boolean;
  savingClinic: boolean;

  // Editable fields only (v1)
  draftFax: string;
  setDraftFax: (v: string) => void;
  draftWebsite: string;
  setDraftWebsite: (v: string) => void;
  draftAdminEmail: string;
  setDraftAdminEmail: (v: string) => void;

  // Clinic setting dropdown (wired by orchestrator)
  clinicSettingOptions?: IdName[];
  draftClinicSettingId?: string; // keep as string in UI state ("")
  setDraftClinicSettingId?: (v: string) => void;

  clinicWebsiteHref: string | null;
  clinicError: string | null;
  clinicSavedAt: string | null;

  // Allow parent to pass async save handler without TS friction
  onSaveClinicCore: () => void | Promise<void>;
}) {
  const {
    clinic,
    editMode,
    savingClinic,

    draftFax,
    setDraftFax,
    draftWebsite,
    setDraftWebsite,
    draftAdminEmail,
    setDraftAdminEmail,

    clinicSettingOptions = [],
    draftClinicSettingId = "",
    setDraftClinicSettingId,

    clinicWebsiteHref,
    clinicError,
    clinicSavedAt,
    onSaveClinicCore,
  } = props;

  const canEditClinicSetting = !!setDraftClinicSettingId;

  return (
    <details className="ca-clinic-profile__panel" open>
      <summary className="ca-clinic-profile__summary">
        <div>
          <div className="ca-clinic-profile__sectionTitle">Clinic information</div>
          <div className="ca-clinic-profile__hint">Clinic admin-managed</div>
        </div>
      </summary>

      {/* v1 note: lock basic clinic identity fields */}
      <div className="ca-clinic-profile__row">
        <div className="ca-clinic-profile__label"></div>
        <div className="ca-clinic-profile__value ca-clinic-profile__help">
          Basic clinic information (name, address, city, postal code, phone) is
          verified and can’t be edited here.
        </div>
      </div>

      {/* Clinic setting (FHO/FHG/etc.) */}
      <div className="ca-clinic-profile__row">
        <div className="ca-clinic-profile__label">Clinic setting</div>
        <div className="ca-clinic-profile__value">
          {!editMode ? (
            clinic.clinicSettingName || "—"
          ) : canEditClinicSetting ? (
            <select
              className="ca-clinic-profile__select"
              value={draftClinicSettingId}
              disabled={savingClinic}
              onChange={(e) => setDraftClinicSettingId(e.target.value)}
            >
              <option value="">—</option>
              {clinicSettingOptions.map((opt) => (
                <option key={opt.id} value={String(opt.id)}>
                  {opt.name}
                </option>
              ))}
            </select>
          ) : (
            clinic.clinicSettingName || "—"
          )}
        </div>
      </div>

      <div className="ca-clinic-profile__row">
        <div className="ca-clinic-profile__label">Clinic name</div>
        <div className="ca-clinic-profile__value">{clinic.name || "—"}</div>
      </div>

      <div className="ca-clinic-profile__row">
        <div className="ca-clinic-profile__label">Address line 1</div>
        <div className="ca-clinic-profile__value">{clinic.addressLine1 || "—"}</div>
      </div>

      <div className="ca-clinic-profile__row">
        <div className="ca-clinic-profile__label">Address line 2</div>
        <div className="ca-clinic-profile__value">{clinic.addressLine2 || "—"}</div>
      </div>

      <div className="ca-clinic-profile__row">
        <div className="ca-clinic-profile__label">City</div>
        <div className="ca-clinic-profile__value">{clinic.cityName || "—"}</div>
      </div>

      <div className="ca-clinic-profile__row">
        <div className="ca-clinic-profile__label">Postal code</div>
        <div className="ca-clinic-profile__value">{clinic.postalCode || "—"}</div>
      </div>

      <div className="ca-clinic-profile__row">
        <div className="ca-clinic-profile__label">Phone</div>
        <div className="ca-clinic-profile__value">{clinic.phone || "—"}</div>
      </div>

      <div className="ca-clinic-profile__row">
        <div className="ca-clinic-profile__label">Fax</div>
        <div className="ca-clinic-profile__value">
          {!editMode ? (
            clinic.fax || "—"
          ) : (
            <input
              className="ca-clinic-profile__input"
              type="text"
              value={draftFax}
              disabled={savingClinic}
              onChange={(e) => setDraftFax(e.target.value)}
            />
          )}
        </div>
      </div>

      <div className="ca-clinic-profile__row">
        <div className="ca-clinic-profile__label">Website</div>
        <div className="ca-clinic-profile__value">
          {!editMode ? (
            clinicWebsiteHref ? (
              <a href={clinicWebsiteHref} target="_blank" rel="noreferrer">
                {clinic.website}
              </a>
            ) : (
              "—"
            )
          ) : (
            <>
              <input
                className="ca-clinic-profile__input"
                type="url"
                value={draftWebsite}
                disabled={savingClinic}
                onChange={(e) => setDraftWebsite(e.target.value)}
                placeholder="https://example.com"
              />
              <div className="ca-clinic-profile__help">
                Must start with http:// or https://
              </div>
            </>
          )}
        </div>
      </div>

      <div className="ca-clinic-profile__row">
        <div className="ca-clinic-profile__label">Administration email</div>
        <div className="ca-clinic-profile__value">
          {!editMode ? (
            clinic.administrationEmail || "—"
          ) : (
            <input
              className="ca-clinic-profile__input"
              type="email"
              value={draftAdminEmail}
              disabled={savingClinic}
              onChange={(e) => setDraftAdminEmail(e.target.value)}
              placeholder="admin@clinic.ca"
            />
          )}
        </div>
      </div>

      {clinicError ? (
        <div className="ca-clinic-profile__row">
          <div className="ca-clinic-profile__label"></div>
          <div className="ca-clinic-profile__value ca-clinic-profile__row-error">
            {clinicError}
          </div>
        </div>
      ) : null}

      {editMode ? (
        <div className="ca-clinic-profile__row">
          <div className="ca-clinic-profile__label"></div>
          <div className="ca-clinic-profile__value ca-clinic-profile__save-row">
            <button
              className="ca-clinic-profile__btn"
              onClick={onSaveClinicCore}
              disabled={savingClinic}
            >
              {savingClinic ? "Saving…" : "Save Clinic Info"}
            </button>

            {clinicSavedAt ? (
              <span className="ca-clinic-profile__savedAt">
                Saved {formatSavedAt(clinicSavedAt)}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
    </details>
  );
}
